import types from '../../mutation-types';
import ConversationApi from '../../../api/inbox/conversation';
import MessageApi from '../../../api/inbox/message';
import dinastiaWhatsappChannel from 'dashboard/api/channel/dinastiaWhatsappChannel';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
import {
  buildConversationList,
  setContacts,
  isOnMentionsView,
  isOnUnattendedView,
  isOnFoldersView,
} from './helpers/actionHelpers';
import messageReadActions from './actions/messageReadActions';
import messageTranslateActions from './actions/messageTranslateActions';
import * as Sentry from '@sentry/vue';
import {
  handleVoiceCallCreated,
  handleVoiceCallUpdated,
} from 'dashboard/helper/voice';

export const hasMessageFailedWithExternalError = pendingMessage => {
  // This helper is used to check if the message has failed with an external error.
  // We have two cases
  // 1. Messages that fail from the UI itself (due to large attachments or a failed network):
  //    In this case, the message will have a status of failed but no external error. So we need to create that message again
  // 2. Messages sent from Chatwoot but failed to deliver to the customer for some reason (user blocking or client system down):
  //    In this case, the message will have a status of failed and an external error. So we need to retry that message
  const { content_attributes: contentAttributes, status } = pendingMessage;
  const externalError = contentAttributes?.external_error ?? '';
  return status === MESSAGE_STATUS.FAILED && externalError !== '';
};

// Duração da janela de mensagens por canal (espelha
// Conversations::MessageWindowService no backend). Em segundos.
// Retorna null para canais sem janela.
const messagingWindowSeconds = inbox => {
  if (!inbox) return null;
  const HOUR = 3600;
  switch (inbox.channel_type) {
    case 'Channel::Whatsapp':
      return 24 * HOUR;
    case 'Channel::Tiktok':
      return 48 * HOUR;
    case 'Channel::TwilioSms':
      return inbox.medium === 'whatsapp' ? 24 * HOUR : null;
    case 'Channel::FacebookPage':
    case 'Channel::Instagram':
      return 24 * HOUR; // padrão (config human-agent pode estender p/ 7d)
    case 'Channel::Api': {
      const w = inbox.additional_attributes?.agent_reply_time_window;
      return w ? Number(w) * HOUR : null;
    }
    default:
      return null;
  }
};

// actions
const actions = {
  getConversation: async ({ commit }, conversationId) => {
    const response = await ConversationApi.show(conversationId);
    commit(types.UPDATE_CONVERSATION, response.data);
    commit(`contacts/${types.SET_CONTACT_ITEM}`, response.data.meta.sender);
  },

  // Busca UMA pagina para um contexto (arquitetura de contextos): alimenta
  // o cache de dados e devolve o payload — quem registra ordem/cursor e o
  // conversationResults, fora daqui. Nao toca em paginador legado.
  fetchConversationsPage: async ({ commit, dispatch }, params) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const {
        data: { data },
      } = await ConversationApi.get(params);
      const { payload: conversationList } = data;
      commit(types.SET_ALL_CONVERSATION, conversationList);
      dispatch('conversationStats/get', params, { root: true });
      dispatch(
        'conversationLabels/setBulkConversationLabels',
        conversationList
      );
      setContacts(commit, conversationList);
      return conversationList;
    } finally {
      commit(types.CLEAR_LIST_LOADING_STATUS);
    }
  },

  fetchAllConversations: async ({ commit, state, dispatch }) => {
    commit(types.SET_LIST_LOADING_STATUS);
    // Sem o try/finally, uma falha de rede deixava o flag preso em true:
    // o guard do load-more bloqueava para sempre e o sentinela de fim de
    // lista (v-else com o loading) nem existia mais — lista "travada".
    try {
      const params = state.conversationFilters;
      const {
        data: { data },
      } = await ConversationApi.get(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        params.assigneeType
      );
    } finally {
      commit(types.CLEAR_LIST_LOADING_STATUS);
    }
  },

  fetchFilteredConversations: async ({ commit, dispatch }, params) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const { data } = await ConversationApi.filter(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        'appliedFilters'
      );
    } finally {
      commit(types.CLEAR_LIST_LOADING_STATUS);
    }
  },

  emptyAllConversations({ commit }) {
    commit(types.EMPTY_ALL_CONVERSATION);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchPreviousMessages: async ({ commit }, data) => {
    const {
      data: { meta, payload },
    } = await MessageApi.getPreviousMessages(data);
    commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
      id: data.conversationId,
      data: meta,
    });
    commit(types.SET_PREVIOUS_CONVERSATIONS, {
      id: data.conversationId,
      data: payload,
    });
    if (!payload.length) {
      commit(types.SET_ALL_MESSAGES_LOADED, data.conversationId);
    }
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    let attachments = [];

    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      attachments = data.payload;
    } catch (error) {
      // in case of error, log the error and continue
      Sentry.setContext('Conversation', {
        id: conversationId,
      });
      Sentry.captureException(error);
    } finally {
      // we run the commit even if the request fails
      // this ensures that the `attachment` variable is always present on chat
      commit(types.SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: attachments,
      });
    }
  },

  syncActiveConversationMessages: async (
    { commit, state, dispatch },
    { conversationId }
  ) => {
    const { allConversations, syncConversationsMessages } = state;
    const lastMessageId = syncConversationsMessages[conversationId];
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    // Fetch all the messages after the last message id
    const {
      data: { meta, payload },
    } = await MessageApi.getPreviousMessages({
      conversationId,
      after: lastMessageId,
    });
    commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
      id: conversationId,
      data: meta,
    });
    // Find the messages that are not already present in the store
    const missingMessages = payload.filter(
      message => !messages.find(item => item.id === message.id)
    );
    selectedChat.messages.push(...missingMessages);
    // Sort the messages by created_at
    const sortedMessages = selectedChat.messages.sort((a, b) => {
      return new Date(a.created_at) - new Date(b.created_at);
    });
    commit(types.SET_MISSING_MESSAGES, {
      id: conversationId,
      data: sortedMessages,
    });
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: null,
    });
    dispatch('markMessagesRead', { id: conversationId }, { root: true });
  },

  setConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allConversations } = state;
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    const lastMessage = messages.last();
    if (!lastMessage) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: lastMessage.id,
    });
  },

  async setActiveChat({ commit, dispatch, rootGetters }, { data, after }) {
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    commit(types.TRACK_CONVERSATION_VIEW, data.id);
    commit(types.CLEAR_ALL_MESSAGES_LOADED, data.id);
    if (data.dataFetched === undefined) {
      // Try bundle endpoint first (1 request instead of 6)
      try {
        const { data: bundle } = await ConversationApi.getBundle(
          data.display_id || data.id
        );
        // Populate messages
        if (bundle.messages?.payload?.length) {
          commit(types.SET_PREVIOUS_CONVERSATIONS, {
            id: data.id,
            data: bundle.messages.payload,
          });
          commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
            id: data.id,
            data: bundle.messages.meta,
          });
        }
        if (!bundle.messages?.payload?.length) {
          commit(types.SET_ALL_MESSAGES_LOADED, data.id);
        }
        // Populate contact
        if (bundle.contact) {
          commit(`contacts/${types.SET_CONTACT_ITEM}`, bundle.contact, {
            root: true,
          });
        }
        // Populate contact conversations
        if (bundle.contact_conversations && data.contact?.id) {
          commit(
            `contactConversations/${types.SET_CONTACT_CONVERSATIONS}`,
            { id: data.contact.id, data: bundle.contact_conversations },
            { root: true }
          );
        }
        // Populate labels
        if (bundle.labels) {
          commit(
            `conversationLabels/${types.SET_CONVERSATION_LABELS}`,
            { id: data.id, data: bundle.labels },
            { root: true }
          );
        }
        // Populate assignable agents
        if (bundle.assignable_agents && data.inbox_id) {
          commit(
            'inboxAssignableAgents/SET_INBOX_ASSIGNABLE_AGENTS',
            {
              inboxId: String(data.inbox_id),
              members: bundle.assignable_agents,
            },
            { root: true }
          );
        }
      } catch {
        // Fallback to individual fetch if bundle fails
        const firstMessage = data.messages?.[0];
        await dispatch('fetchPreviousMessages', {
          after,
          before: firstMessage?.id,
          conversationId: data.id,
        });
      }
      commit(types.SET_CHAT_DATA_FETCHED, data.id);
    }

    // Auto mark_read for Dinastia WhatsApp conversations
    try {
      const inbox = rootGetters['inboxes/getInbox'](data.inbox_id);
      if (inbox?.channel_type === INBOX_TYPES.DINASTIA_WHATSAPP) {
        const messages = data.messages || [];
        const lastIncoming = [...messages]
          .reverse()
          .find(m => m.message_type === 0 && m.source_id);
        if (lastIncoming?.id) {
          dinastiaWhatsappChannel.markRead(data.inbox_id, {
            messageId: lastIncoming.id,
          });
        }
      }
    } catch {
      // Best-effort: don't block conversation opening
    }
  },

  // A marcação otimista e o desfazer moram aqui, e não em cada tela: quando o
  // servidor recusa a atribuição, o cabeçalho voltava a mostrar um responsável
  // que nunca existiu, e a conversa parecia ter sido desatribuída sozinha
  // depois. Quem chama só precisa tratar o erro que sobe.
  assignAgent: async (
    { dispatch, getters },
    { conversationId, agentId, agent }
  ) => {
    const anterior =
      getters.getConversationById(conversationId)?.meta?.assignee;
    dispatch('setCurrentChatAssignee', {
      conversationId,
      assignee: agent || (agentId ? { id: agentId } : null),
    });
    try {
      const response = await ConversationApi.assignAgent({
        conversationId,
        agentId,
      });
      dispatch('setCurrentChatAssignee', {
        conversationId,
        assignee: response.data,
      });
    } catch (error) {
      dispatch('setCurrentChatAssignee', {
        conversationId,
        assignee: anterior || null,
      });
      throw error;
    }
  },

  setCurrentChatAssignee({ commit }, { conversationId, assignee }) {
    commit(types.ASSIGN_AGENT, { conversationId, assignee });
  },

  assignTeam: async ({ dispatch }, { conversationId, teamId }) => {
    const response = await ConversationApi.assignTeam({
      conversationId,
      teamId,
    });
    dispatch('setCurrentChatTeam', { team: response.data, conversationId });
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  toggleStatus: async (
    { commit },
    { conversationId, status, snoozedUntil = null, customAttributes = null }
  ) => {
    // Update custom attributes first if provided
    if (customAttributes) {
      await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
        conversationId,
        customAttributes,
      });
    }

    const {
      data: {
        payload: {
          current_status: updatedStatus,
          snoozed_until: updatedSnoozedUntil,
        } = {},
      } = {},
    } = await ConversationApi.toggleStatus({
      conversationId,
      status,
      snoozedUntil,
    });
    commit(types.CHANGE_CONVERSATION_STATUS, {
      conversationId,
      status: updatedStatus,
      snoozedUntil: updatedSnoozedUntil,
    });
  },

  toggleBot: async ({ commit }, { conversationId }) => {
    const { data } = await ConversationApi.toggleBot({ conversationId });
    commit(types.UPDATE_CONVERSATION, {
      id: conversationId,
      bot_active: data.bot_active,
    });
  },

  // Controle de 3 estados da orquestracao (assume/disable/reactivate/return_to_triage)
  setBotState: async ({ commit }, { conversationId, stateAction }) => {
    const { data } = await ConversationApi.setBotState({
      conversationId,
      stateAction,
    });
    commit(types.UPDATE_CONVERSATION, {
      id: conversationId,
      bot_active: data.bot_active,
      additional_attributes: data.additional_attributes,
    });
  },

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async ({ commit }, pendingMessage) => {
    const { conversation_id: conversationId, id } = pendingMessage;
    try {
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        status: MESSAGE_STATUS.PROGRESS,
      });
      const response = hasMessageFailedWithExternalError(pendingMessage)
        ? await MessageApi.retry(conversationId, id)
        : await MessageApi.create(pendingMessage);
      commit(types.ADD_MESSAGE, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.error
        : undefined;
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        meta: {
          error: errorMessage,
        },
        status: MESSAGE_STATUS.FAILED,
      });
      throw error;
    }
  },

  addMessage({ commit, rootGetters }, message) {
    commit(types.ADD_MESSAGE, message);
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.SET_CONVERSATION_CAN_REPLY, {
        conversationId: message.conversation_id,
        canReply: true,
      });
      // Reabre/estende a janela de 24h em tempo real (antes só atualizava no F5).
      const windowSeconds = messagingWindowSeconds(
        rootGetters['inboxes/getInbox'](message.inbox_id)
      );
      if (windowSeconds && message.created_at) {
        commit(types.SET_CONVERSATION_MESSAGING_WINDOW, {
          conversationId: message.conversation_id,
          expiresAt: Number(message.created_at) + windowSeconds,
        });
      }
      commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    }
    handleVoiceCallCreated(message, rootGetters?.getCurrentUserID);
  },

  updateMessage({ commit, rootGetters }, message) {
    commit(types.ADD_MESSAGE, message);
    commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    handleVoiceCallUpdated(commit, message, rootGetters?.getCurrentUserID);
  },

  deleteMessage: async function deleteLabels(
    { commit },
    { conversationId, messageId }
  ) {
    try {
      const { data } = await MessageApi.delete(conversationId, messageId);
      commit(types.ADD_MESSAGE, data);
      commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  deleteConversation: async ({ commit, dispatch }, conversationId) => {
    try {
      await ConversationApi.delete(conversationId);
      commit(types.DELETE_CONVERSATION, conversationId);
      dispatch('conversationStats/get', {}, { root: true });
    } catch (error) {
      throw new Error(error);
    }
  },

  addConversation({ commit, state, dispatch, rootState }, conversation) {
    const { currentInbox, appliedFilters } = state;
    const {
      inbox_id: inboxId,
      meta: { sender },
    } = conversation;
    const hasAppliedFilters = !!appliedFilters.length;
    const isMatchingInboxFilter =
      !currentInbox || Number(currentInbox) === inboxId;
    if (
      !hasAppliedFilters &&
      !isOnFoldersView(rootState) &&
      !isOnMentionsView(rootState) &&
      !isOnUnattendedView(rootState) &&
      isMatchingInboxFilter
    ) {
      commit(types.ADD_CONVERSATION, conversation);
      dispatch('contacts/setContact', sender);
    }
  },

  addMentions({ dispatch, rootState }, conversation) {
    if (isOnMentionsView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  updateConversation({ commit, dispatch }, conversation) {
    const {
      meta: { sender },
    } = conversation;
    commit(types.UPDATE_CONVERSATION, conversation);

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });

    dispatch('contacts/setContact', sender);
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async ({ commit }, conversationId) => {
    await ConversationApi.mute(conversationId);
    commit(types.MUTE_CONVERSATION);
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    await ConversationApi.unmute(conversationId);
    commit(types.UNMUTE_CONVERSATION);
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    try {
      await ConversationApi.sendEmailTranscript({ conversationId, email });
    } catch (error) {
      throw new Error(error);
    }
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    const response = await ConversationApi.updateCustomAttributes({
      conversationId,
      customAttributes,
    });
    const { custom_attributes } = response.data;
    commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
      conversationId,
      customAttributes: custom_attributes,
    });
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  setChatListFilters({ commit }, data) {
    commit(types.SET_CHAT_LIST_FILTERS, data);
  },

  updateChatListFilters({ commit }, data) {
    commit(types.UPDATE_CHAT_LIST_FILTERS, data);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    await ConversationApi.togglePriority({
      conversationId,
      priority,
    });

    dispatch('setCurrentChatPriority', {
      priority,
      conversationId,
    });
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  getForwardableInboxes: async (_, conversationId) => {
    const response =
      await ConversationApi.getForwardableInboxes(conversationId);
    return response.data;
  },

  forwardToInbox: async (_, { conversationId, inboxId }) => {
    const response = await ConversationApi.forwardToInbox({
      conversationId,
      inboxId,
    });
    return response.data;
  },

  setContextMenuChatId({ commit }, chatId) {
    commit(types.SET_CONTEXT_MENU_CHAT_ID, chatId);
  },

  getInboxCaptainAssistantById: async ({ commit }, conversationId) => {
    const response = await ConversationApi.getInboxAssistant(conversationId);
    commit(types.SET_INBOX_CAPTAIN_ASSISTANT, response.data);
  },

  ...messageReadActions,
  ...messageTranslateActions,
};

export default actions;
